'use client';

import Link from '@/components/NoPrefetchLink';
import { useEffect, useMemo, useState } from 'react';
import type { NpbAwardMaster, NpbAwardsByCategory } from '@/lib/types';
import { withLocalePrefix } from '@/lib/localePath';
import type { Locale } from '@/lib/localePath';
import PageEmptyState from '@/components/PageEmptyState';
import { ScrollToTopButton } from './ScrollToTopButton';
import '@/styles/InfoPage.css';

type Props = { locale: Locale; initialCategories?: NpbAwardsByCategory[] };

const awardSlug = (awardId: string): string => awardId.replace(/ /g, '-');

const CATEGORY_NAMES: Record<string, [string, string, string]> = {
    '記者投票': ['記者投票', 'Media Vote', 'Voto de la Prensa'],
    '月次表彰': ['月次表彰', 'Monthly Awards', 'Premios Mensuales'],
    '特別表彰': ['特別表彰', 'Special Awards', 'Premios Especiales'],
    'その他': ['その他', 'Other', 'Otros'],
};
const CATEGORY_ORDER = ['記者投票', '月次表彰', '特別表彰', 'その他'];

export default function NpbAwardsClient({ locale, initialCategories }: Props) {
    const [categories, setCategories] = useState<NpbAwardsByCategory[]>(initialCategories ?? []);
    const [loading, setLoading] = useState(false);
    const [activeCategory, setActiveCategory] = useState<string>('');

    const t = (ja: string, en: string, es: string) =>
        locale === 'en' ? en : locale === 'es' ? es : ja;

    const awardName = (award: NpbAwardMaster) =>
        locale === 'en'
            ? (award.awardNameEn || award.awardId)
            : locale === 'es'
            ? (award.awardNameEs || award.awardNameEn || award.awardId)
            : (award.awardNameJa || award.awardId);

    const categoryName = (cat: string) => {
        const entry = CATEGORY_NAMES[cat];
        return entry ? t(entry[0], entry[1], entry[2]) : cat;
    };

    const orderedCategories = useMemo(() => {
        const orderIndex = new Map<string, number>(CATEGORY_ORDER.map((category, index) => [category, index]));
        return [...categories]
            .filter((cat) => cat.awards.length > 0)
            .sort((a, b) => {
                const indexA = orderIndex.get(a.category);
                const indexB = orderIndex.get(b.category);
                if (indexA !== undefined && indexB !== undefined) return indexA - indexB;
                if (indexA !== undefined) return -1;
                if (indexB !== undefined) return 1;
                return a.category.localeCompare(b.category, 'ja');
            });
    }, [categories]);
    const emptyStateLinks = [
        {
            href: withLocalePrefix(locale, '/npb'),
            label: t('プロ野球（NPB）トップ', 'NPB home', 'Inicio NPB'),
            note: t('NPBトップへ。', 'Open the NPB home page.', 'Abre la página principal de NPB.'),
        },
        {
            href: withLocalePrefix(locale, '/awards'),
            label: t('メジャーリーグ（MLB）アワード', 'MLB awards', 'Premios de MLB'),
            note: t('MLBアワード一覧へ。', 'Open the MLB awards list.', 'Abre la lista de premios de MLB.'),
        },
        {
            href: withLocalePrefix(locale, '/'),
            label: t('ホームへ戻る', 'Back to home', 'Volver al inicio'),
            note: t('ホームへ。', 'Go back to home.', 'Vuelve al inicio.'),
        },
    ];


    useEffect(() => {
        if (orderedCategories.length === 0) {
            if (activeCategory !== '') setActiveCategory('');
            return;
        }
        if (!orderedCategories.some((cat) => cat.category === activeCategory)) {
            setActiveCategory(orderedCategories[0].category);
        }
    }, [orderedCategories, activeCategory]);

    if (loading) {
        return (
            <div className="info-page">
                <p>{t('読み込み中...', 'Loading...', 'Cargando...')}</p>
            </div>
        );
    }

    const effectiveCategory = orderedCategories.find((cat) => cat.category === activeCategory) ?? orderedCategories[0] ?? null;

    return (
        <div className="info-page">
            <nav className="awards-breadcrumb">
                <Link href={withLocalePrefix(locale, '/')}>{t('ホーム', 'Home', 'Inicio')}</Link>
                {' › '}
                <span>{t('プロ野球（NPB）アワード', 'Nippon Professional Baseball (NPB) Awards', 'Premios de Béisbol Profesional Japonés (NPB)')}</span>
            </nav>
            <h1>{t('プロ野球（NPB）アワード', 'Nippon Professional Baseball (NPB) Awards', 'Premios de Béisbol Profesional Japonés (NPB)')}</h1>

            {orderedCategories.length === 0 ? (
                <PageEmptyState
                    id="npb-awards-empty-state"
                    title={t('表示できるアワードがありません。', 'No award categories are available.', 'No hay categorías de premios disponibles.')}
                    paragraphs={[
                        t(
                            '表示できるアワードカテゴリがありません。',
                            'No award categories are available.',
                            'No hay categorías de premios disponibles.',
                        ),
                        t(
                            'NPBトップやMLBアワード一覧から探してください。',
                            'Use the NPB home page or the MLB awards list.',
                            'Usa la página principal de NPB o la lista de premios de MLB.',
                        ),
                    ]}
                    links={emptyStateLinks}
                    linksLabel={t('関連リンク', 'Related links', 'Enlaces relacionados')}
                />
            ) : (
                <>
                    {orderedCategories.length > 1 && (
                        <div
                            className="info-tabs"
                            role="tablist"
                            aria-label={t('プロ野球（NPB）アワードカテゴリ', 'Nippon Professional Baseball (NPB) award categories', 'Categorías de premios de Béisbol Profesional Japonés (NPB)')}
                        >
                            {orderedCategories.map((cat) => (
                                <button
                                    key={cat.category}
                                    type="button"
                                    role="tab"
                                    aria-selected={effectiveCategory?.category === cat.category}
                                    className={`info-tab-button ${effectiveCategory?.category === cat.category ? 'active' : ''}`}
                                    onClick={() => setActiveCategory(cat.category)}
                                >
                                    {categoryName(cat.category)}
                                </button>
                            ))}
                        </div>
                    )}

                    {effectiveCategory && (
                        <section className="awards-category-section">
                            {orderedCategories.length <= 1 && (
                                <h2 className="awards-category-title">{categoryName(effectiveCategory.category)}</h2>
                            )}
                            <ul className="awards-list">
                                {effectiveCategory.awards.map((award) => (
                                    <li key={award.awardId} className="awards-list-item">
                                        <Link href={withLocalePrefix(locale, `/npb-awards/${awardSlug(award.awardId)}`)}>
                                            {awardName(award)}
                                        </Link>
                                        {award.descriptionJa && locale === 'ja' && (
                                            <span className="awards-description">{award.descriptionJa}</span>
                                        )}
                                        {award.descriptionEn && locale === 'en' && (
                                            <span className="awards-description">{award.descriptionEn}</span>
                                        )}
                                        {award.descriptionEs && locale === 'es' && (
                                            <span className="awards-description">{award.descriptionEs}</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </>
            )}
            <ScrollToTopButton />
        </div>
    );
}
